import React, { useRef, useState, useEffect } from 'react';
import { useLanguage } from '../context/LanguageContext';
import { galleryImages } from '../data/mock';
import { MapPin, Calendar, ChevronLeft, ChevronRight, ArrowRight } from 'lucide-react';
import { Button } from './ui/button';
import { useNavigate } from 'react-router-dom';

const GallerySection = () => {
  const { t } = useLanguage();
  const navigate = useNavigate();
  const scrollContainerRef = useRef(null);
  const [canScrollLeft, setCanScrollLeft] = useState(false);
  const [canScrollRight, setCanScrollRight] = useState(true);

  const checkScrollButtons = () => {
    const container = scrollContainerRef.current;
    if (container) {
      setCanScrollLeft(container.scrollLeft > 0);
      setCanScrollRight(
        container.scrollLeft < container.scrollWidth - container.clientWidth - 10
      );
    }
  };

  useEffect(() => {
    const container = scrollContainerRef.current;
    if (container) {
      container.addEventListener('scroll', checkScrollButtons);
      checkScrollButtons();
      return () => container.removeEventListener('scroll', checkScrollButtons);
    }
  }, []);

  const scroll = (direction) => {
    const container = scrollContainerRef.current;
    if (container) {
      const scrollAmount = 320; // Width of one image + gap
      container.scrollBy({
        left: direction === 'left' ? -scrollAmount : scrollAmount,
        behavior: 'smooth'
      });
    }
  };

  const previewImages = galleryImages.slice(0, 10); // Show first 10 in preview

  return (
    <section id="gallery" className="py-24 bg-[#f7f5f2]">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        {/* Header */}
        <div className="text-center max-w-3xl mx-auto mb-12">
          <span className="inline-block text-[#8B1C1C] font-semibold text-sm uppercase tracking-wider mb-4">
            {t('Gallery', 'Galeri')}
          </span>
          <h2 className="text-3xl sm:text-4xl font-bold text-[#1e1919] mb-6">
            Graduation 2026
          </h2>
          <div className="flex flex-col sm:flex-row items-center justify-center gap-4 text-[#736c64]">
            <div className="flex items-center gap-2">
              <MapPin className="w-5 h-5 text-[#8B1C1C]" />
              <span>Pala Ballroom Suite Hotel Surabaya</span>
            </div>
            <div className="flex items-center gap-2">
              <Calendar className="w-5 h-5 text-[#8B1C1C]" />
              <span>2026</span>
            </div>
          </div>
        </div>

        {/* Carousel Container */}
        <div className="relative">
          {/* Left Arrow */}
          {canScrollLeft && (
            <button
              onClick={() => scroll('left')}
              className="absolute left-0 top-1/2 -translate-y-1/2 z-10 w-12 h-12 bg-white/90 hover:bg-white shadow-lg rounded-full flex items-center justify-center transition-all hover:scale-110 -ml-4"
            >
              <ChevronLeft className="w-6 h-6 text-[#8B1C1C]" />
            </button>
          )}

          {/* Scrollable Gallery */}
          <div
            ref={scrollContainerRef}
            className="flex gap-4 overflow-x-auto scrollbar-hide pb-4 scroll-smooth"
            style={{
              scrollbarWidth: 'none',
              msOverflowStyle: 'none',
              WebkitOverflowScrolling: 'touch'
            }}
          >
            {previewImages.map((image, index) => (
              <div
                key={index}
                className="flex-shrink-0 w-72 h-72 sm:w-80 sm:h-80 rounded-2xl overflow-hidden cursor-pointer group"
                onClick={() => navigate('/gallery')}
              >
                <img
                  src={image}
                  alt={`Graduation 2026 - ${index + 1}`}
                  className="w-full h-full object-cover transition-transform duration-500 group-hover:scale-110"
                  loading="lazy"
                />
              </div>
            ))}

            {/* "See More" Card */}
            {galleryImages.length > 10 && (
              <div
                className="flex-shrink-0 w-72 h-72 sm:w-80 sm:h-80 rounded-2xl bg-[#8B1C1C] flex flex-col items-center justify-center cursor-pointer hover:bg-[#6d1616] transition-colors"
                onClick={() => navigate('/gallery')}
              >
                <span className="text-white/80 text-lg mb-2">
                  +{galleryImages.length - 10}
                </span>
                <span className="text-white font-semibold text-xl">
                  {t('More Photos', 'Foto Lainnya')}
                </span>
              </div>
            )}
          </div>

          {/* Right Arrow */}
          {canScrollRight && (
            <button
              onClick={() => scroll('right')}
              className="absolute right-0 top-1/2 -translate-y-1/2 z-10 w-12 h-12 bg-white/90 hover:bg-white shadow-lg rounded-full flex items-center justify-center transition-all hover:scale-110 -mr-4"
            >
              <ChevronRight className="w-6 h-6 text-[#8B1C1C]" />
            </button>
          )}
        </div>

        {/* Scroll Hint for Mobile */}
        <p className="text-center text-[#736c64] text-sm mt-4 sm:hidden">
          ← {t('Swipe to see more', 'Geser untuk melihat lebih')} →
        </p>

        {/* See More Button */}
        <div className="text-center mt-10">
          <Button
            onClick={() => navigate('/gallery')}
            className="bg-[#8B1C1C] hover:bg-[#6d1616] text-white px-8 py-6 rounded-full font-semibold transition-all"
          >
            {t('View All Photos', 'Lihat Semua Foto')}
            <ArrowRight className="w-5 h-5 ml-2" />
          </Button>
          <p className="text-[#736c64] text-sm mt-3">
            {t(`${galleryImages.length} photos available`, `${galleryImages.length} foto tersedia`)}
          </p>
        </div>
      </div>

      {/* Hide scrollbar CSS */}
      <style>{`
        .scrollbar-hide::-webkit-scrollbar {
          display: none;
        }
      `}</style>
    </section>
  );
};

export default GallerySection;
